import { WMSearchFilter, WMTableColumnShape, WMCheckbox } from '@walkme/wm-ui';
import pluralize from 'pluralize';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import classes from './FilterBox.module.scss';
import cc from 'classcat';
import { AppTableContext } from '../../../AppTableContext';
import clsx from 'clsx';
import { AppTableFilter } from '../../../model';
import { ALL_RECIPIENTS_KEY, END_USERS_RECIPIENTS_KEY } from '@communication-center/data-objects';
import { FilterTypes } from '../../../WMTable/WMTable';
import { ReactComponent as CheckIcon } from '../../../../../../assets/icons/check-icon.svg';
import { useTranslation } from 'react-i18next';

const keyPrefix = 'components.appTable.filterBox';

export const FilterBox = ({
    type = FilterTypes.FILTER,
    filters = [],
    dataKey,
    filterTitle,
    renderFilterItem
}: {
    type?: FilterTypes;
    filters: AppTableFilter[];
    dataKey: string;
    filterTitle: string;
    renderFilterItem?: WMTableColumnShape['renderFilterItem'];
}) => {
    const { t } = useTranslation('', { keyPrefix });
    const [filtersInnerSearchVal, setFiltersInnerSearchVal] = useState('');
    const { tableDataTestid, columns, setFilter } = useContext(AppTableContext);

    const column = useMemo(() => columns?.find?.(column => column.dataKey === dataKey), [columns, dataKey]);

    const toggleFilter = useCallback(
        (value: any) => () => {
            const updatedFilters = filters.map(item => (item.value === value ? { ...item, checked: !item.checked } : item));
            setFilter(dataKey, updatedFilters);
        },
        [dataKey, filters, setFilter]
    );

    const filterBySearch = (item: $TSFixMe) =>
        !filtersInnerSearchVal ? true : item.label.toLowerCase().includes(filtersInnerSearchVal.toLowerCase());

    const handleSelectAll = useCallback(() => {
        const allSelected = filters.every(filter => filter.checked);
        const updatedFilters = filters.map(item => ({ ...item, checked: !allSelected }));
        setFilter(dataKey, updatedFilters);
    }, [dataKey, filters, setFilter]);

    const handleSelectOnce = (value?: string) => () => {
        const updatedFilters = filters.map(item => ({ ...item, checked: item.value === value }));
        setFilter(dataKey, updatedFilters);
    };

    const isSpecialItem = (item: AppTableFilter) => item?.label === ALL_RECIPIENTS_KEY || item?.label === END_USERS_RECIPIENTS_KEY;

    const getLabel = (item: AppTableFilter) => (isSpecialItem(item) ? t(item.label!) : renderFilterItem ? renderFilterItem(item) : item.label);

    const checkedCount = useMemo(() => filters.filter(item => item.checked).length, [filters]);
    const dataTestId = `${tableDataTestid}-filter-box`;

    return (
        <div className={clsx(classes.filterBox, { [classes.hideSearch]: column?.filterMetaData?.hideSearch })} data-testid={dataTestId}>
            {!column?.filterMetaData?.hideSearch && (
                <div className={classes.header}>
                    <WMSearchFilter
                        ds2
                        className={classes.wmSearchFilter}
                        classes={{ input: classes.wmSearchFilterInput }}
                        value={filtersInnerSearchVal}
                        onSearch={(value: string) => setFiltersInnerSearchVal(value)}
                        inputProps={{ 'data-testid': `${dataTestId}-search` } as $TSFixMe}
                        placeholder={t('search')}
                    />
                </div>
            )}
            <div className={classes.body}>
                {!filtersInnerSearchVal && type !== FilterTypes.DATASOURCE && (
                    <div className={cc([classes.filterCheckboxRow, classes.selectAllCheckbox])}>
                        <WMCheckbox
                            ds2
                            size="medium"
                            inputProps={{ 'data-testid': `${dataTestId}-select-all` } as $TSFixMe}
                            formControlLabelProps={{ classes: { root: classes.checkboxLabel, label: classes.checkboxLabelSpan } }}
                            checked={checkedCount === filters.length}
                            indeterminate={checkedCount > 0 && checkedCount < filters.length}
                            label={t('selectAll', {
                                title: `${pluralize.plural(filterTitle)}`,
                                count: filterTitle.length
                            })}
                            onChange={handleSelectAll}
                        />
                    </div>
                )}

                {type === FilterTypes.DATASOURCE ? (
                    <div className={classes.dataSources}>
                        {filters.map(item => (
                            <div
                                data-testid={`${dataTestId}-item-${item.value}`}
                                key={item.value}
                                className={cc([classes.dataSourcesOption, { [classes.isChecked]: item.checked }])}
                                onClick={handleSelectOnce(item.value)}
                            >
                                <div className={classes.dataSourcesOptionLabel}>{item.label}</div>

                                {item.checked && <CheckIcon />}
                            </div>
                        ))}
                    </div>
                ) : (
                    filters.filter(filterBySearch).map(item => (
                        <div
                            key={item.label}
                            className={cc([
                                classes.filterCheckboxRow,
                                { [classes.selectAllCheckbox]: isSpecialItem(item) }
                            ])}
                        >
                            <WMCheckbox
                                ds2
                                size="medium"
                                formControlLabelProps={{ classes: { root: classes.checkboxLabel, label: classes.checkboxLabelSpan } }}
                                checked={!!item.checked}
                                label={getLabel(item)}
                                onChange={toggleFilter(item.value)}
                                inputProps={{ 'data-testid': `${dataTestId}-item-${item.label}` } as $TSFixMe}
                            />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
